// ShoppingCartModal.js
import React, { useEffect, useMemo, useState } from "react";
import { useShoppingCart } from "./ShoppingCartContext";
import { executeAction } from "../../core/forms/CustomButtons";
import { useAuth } from "../../core/auth/AuthContext";
import usePopup from "../../core/hooks/usePopup";

function asBool(v, def = true) {
  if (v === undefined || v === null) return def;
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

function toNumber(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

function format2(n) {
  const num = Number(n || 0);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
}

function isImageField(fieldName) {
  return String(fieldName || "").toUpperCase().includes("IMAGE");
}

function buildImageSrc(value) {
  if (typeof value !== "string" || !value) return null;
  if (value.startsWith("data:")) return value;
  const isJpeg = value.startsWith("/9j");
  const mime = isJpeg ? "image/jpeg" : "image/png";
  return `data:${mime};base64,${value}`;
}

function getFirstExisting(obj, keys = []) {
  for (const k of keys) {
    if (k && obj && Object.prototype.hasOwnProperty.call(obj, k) && obj[k] != null) return obj[k];
  }
  return null;
}

export default function ShoppingCartModal() {
  const { user, companyId } = useAuth();
  const localPopup = usePopup(3000);

  const { isOpen, closeCart, items, removeItem, setQuantity, purchase, cartConfig, refresh } = useShoppingCart();

  // Busy overlay (whole cart popup)
  const [busyState, setBusyState] = useState({ on: false, message: "" });

  // Success dialog after purchase
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    title: "Purchase order created",
    orderNumber: null,
    text: "",
  });

  const [errorDialog, setErrorDialog] = useState({
    open: false,
    title: "Unable to continue",
    text: "",
  });

  const showCartError = (message, title = "Unable to continue") => {
    setErrorDialog({
      open: true,
      title,
      text: message || "An unexpected error occurred.",
    });
  };

  // -------------------------
  // Header info state
  // -------------------------
  const [orderHeader, setOrderHeader] = useState({
    loading: false,
    values: {},
    options: {},
    error: null,
  });

  const orderFields = cartConfig?.OrderFields || null;
  const headerFn = cartConfig?.shopping_cart_headerInfo_function;
  const headerConnId = cartConfig?.shopping_cart_headerInfo_connection_id;

  const userEmail = user?.email || "";
  const userName = `${user?.firstname || ""} ${user?.lastname || ""}`.trim();

  // ---- config from headerButtons JSON ----
  const priceField = String(cartConfig?.priceField || "").trim();
  const currencyValue = cartConfig?.currencyValue != null ? String(cartConfig.currencyValue) : "";
  const postPriceValueText = cartConfig?.postPriceValueText != null ? String(cartConfig.postPriceValueText) : "";

  const showCurrency = currencyValue.trim().length > 0;
  const showPostText = postPriceValueText.trim().length > 0;

  const subButtons = useMemo(() => {
    const arr = Array.isArray(cartConfig?.subButtons) ? cartConfig.subButtons : [];
    return arr.filter((b) => asBool(b.visible, true)).sort((a, b) => toNumber(a.order, 0) - toNumber(b.order, 0));
  }, [cartConfig]);

  // normalize item payload
  const normalized = useMemo(() => {
    return (items || []).map((it) => {
      const fj = it?.fields_json || {};
      const fields = Array.isArray(fj.fieldsToShow)
        ? fj.fieldsToShow
        : String(fj.fieldsToShow || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

      const data = fj && typeof fj === "object" && fj.data && typeof fj.data === "object" ? fj.data : {};
      return { ...it, _fields: fields, _data: data };
    });
  }, [items]);

  // infer display fields (ERP-flexible)
  const inferredKeys = useMemo(() => {
    const sample = normalized?.[0]?._data || {};
    const allKeys = Object.keys(sample || {});
    const findByIncludes = (needle) => allKeys.find((k) => String(k).toUpperCase().includes(needle)) || "";
    return {
      imageKey: findByIncludes("THUMB_IMAGE") || findByIncludes("IMAGE"),
      codeKey: findByIncludes("ITEM_CODE") || findByIncludes("CODE"),
      descKey: findByIncludes("ITEM_DESCRIPTION") || findByIncludes("DESCRIPTION"),
    };
  }, [normalized]);

  // total
  const total = useMemo(() => {
    let t = 0;
    for (const it of normalized) {
      let raw = null;
      if (priceField) raw = it._data?.[priceField];
      if (raw == null) {
        const fallbackField = (it._fields || []).find((f) => String(f).toUpperCase().includes("PRICE"));
        raw = fallbackField ? it._data?.[fallbackField] : null;
      }
      const unit = raw != null ? Number(raw) : 0;
      t += unit * Number(it.quantity || 1);
    }
    return t;
  }, [normalized, priceField]);

  // ✅ Only show non-fixed fields
  const orderDetailsEntries = useMemo(() => {
    if (!orderFields) return [];
    return Object.entries(orderFields).filter(([_, def]) => {
      const mode = String(def?.mode || "").trim().toLowerCase();
      return mode !== "fixed";
    });
  }, [orderFields]);

  // ------------------------------------------------------------
  // Header dependencies / cascading dropdowns
  // ------------------------------------------------------------

  /**
   * Zoek alle velden die direct of indirect afhankelijk zijn
   * van een gewijzigd header-veld.
   *
   * Voorbeeld:
   * FK_RELATION
   *   -> FK_DESTINATIONADDRESS
   *
   * doordat FK_DESTINATIONADDRESS heeft:
   * queryParams: ["HEADER:FK_RELATION"]
   */
  const getDependentFields = (changedField) => {
    if (!orderFields || !changedField) return [];

    const result = new Set();

    const visit = (parentField) => {
      const parentToken = `HEADER:${String(parentField).trim().toUpperCase()}`;

      for (const [fieldName, def] of Object.entries(orderFields)) {
        const queryParams = Array.isArray(def?.queryParams)
          ? def.queryParams
          : [];

        const dependsOnParent = queryParams.some((param) => {
          if (typeof param !== "string") return false;

          return param.trim().toUpperCase() === parentToken;
        });

        if (dependsOnParent && !result.has(fieldName)) {
          result.add(fieldName);

          // Ook eventuele afhankelijkheden van dit veld meenemen.
          visit(fieldName);
        }
      }
    };

    visit(changedField);

    return Array.from(result);
  };


  /**
   * Headerinformatie opnieuw ophalen vanuit Ridder.
   *
   * currentValues bevat altijd de actuele gebruikerskeuzes.
   * De backend gebruikt deze vervolgens ook voor HEADER:* queryParams.
   */
  const loadHeaderInfo = async (currentValues = {}) => {
    if (!headerFn || !headerConnId || !orderFields) return;

    try {
      setOrderHeader((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      const res = await executeAction(headerFn, {
        companyId,
        connectionId: headerConnId,
        userEmail,
        popup: localPopup,
        cartConfig,
        orderFields,

        // Zeer belangrijk:
        // actuele waarden meegeven zodat bijvoorbeeld
        // HEADER:FK_RELATION de zojuist gekozen relatie gebruikt.
        orderHeader: {
          values: currentValues,
        },
      });

      if (!res?.success) {
        setOrderHeader((prev) => ({
          ...prev,
          loading: false,
          error: res?.error || "Unable to load order header info.",
        }));
        return;
      }

      setOrderHeader({
        loading: false,
        values: res.values || currentValues || {},
        options: res.options || {},
        error: null,
      });
    } catch (e) {
      setOrderHeader((prev) => ({
        ...prev,
        loading: false,
        error:
          e?.response?.data?.error ||
          e?.message ||
          "Unable to load order header info.",
      }));
    }
  };


  /**
   * Gebruiker wijzigt een header-veld.
   *
   * Als andere velden hiervan afhankelijk zijn:
   * 1. oude waarden wissen;
   * 2. oude dropdown-opties wissen;
   * 3. headerinfo opnieuw ophalen.
   */
  const setOrderFieldValue = async (field, value) => {
    const currentValues = orderHeader?.values || {};
    const dependentFields = getDependentFields(field);

    const nextValues = {
      ...currentValues,
      [field]: value,
    };

    // Oude keuzes van afhankelijke velden zijn niet meer geldig.
    for (const dependentField of dependentFields) {
      delete nextValues[dependentField];
    }

    setOrderHeader((prev) => {
      const nextOptions = {
        ...(prev.options || {}),
      };

      // Oude opties meteen leegmaken, zodat bijvoorbeeld
      // adressen van de vorige relatie niet zichtbaar blijven.
      for (const dependentField of dependentFields) {
        nextOptions[dependentField] = [];
      }

      return {
        ...prev,
        values: nextValues,
        options: nextOptions,
        error: null,
      };
    });

    // Alleen opnieuw naar backend als er daadwerkelijk
    // afhankelijke velden bestaan.
    if (dependentFields.length > 0) {
      await loadHeaderInfo(nextValues);
    }
  };


  // ------------------------------------------------------------
  // Initial header-info load when shopping cart opens
  // ------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    setOrderHeader({
      loading: false,
      values: {},
      options: {},
      error: null,
    });

    (async () => {
      if (!headerFn || !headerConnId || !orderFields) return;

      await loadHeaderInfo({});
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, headerFn, headerConnId, companyId, userEmail]);

  // ✅ NOW we can safely early-return, AFTER all hooks:
  if (!isOpen) return null;

  const startLoading = (message = "") => {
    setBusyState({ on: true, message: message || "" });
  };
  const stopLoading = () => {
    setBusyState({ on: false, message: "" });
  };

  const actionCtxBase = {
    companyId,
    moduleId: cartConfig?.moduleId || null,
    recordId: null,
    userEmail,
    userName,
    popup: {
      ...localPopup,
      flashError: (message) => showCartError(message),
    },

    startLoading,
    stopLoading,

    closeCart,
    refreshCart: refresh,
    cartItems: items,

    orderHeaderValues: orderHeader?.values || {},
    cartConfig,

    purchase,

    // ✅ callback from CustomButtons purchase handler
    onPurchaseSuccess: (result) => {
      const orderNumbers = Array.isArray(result?.orderNumbers)
        ? result.orderNumbers.filter(
            (value) => value !== null && value !== undefined && value !== ""
          )
        : [];

      const orderNumber =
        orderNumbers.length > 0
          ? orderNumbers.join(", ")
          : result?.orderNumber ?? null;

      const createdOrderCount = Number(
        result?.createdOrderCount ||
          orderNumbers.length ||
          (orderNumber ? 1 : 0)
      );

      const multipleOrders =
        createdOrderCount > 1 || orderNumbers.length > 1;

      setSuccessDialog({
        open: true,
        title:
          result?.title ||
          (multipleOrders
            ? "Purchase orders created"
            : "Purchase order created"),
        orderNumber,
        text:
          result?.message ||
          (orderNumber
            ? multipleOrders
              ? `Your purchase orders have been created successfully.\nOrder numbers: ${orderNumber}`
              : `Your purchase order has been created successfully.\nOrder number: ${orderNumber}`
            : multipleOrders
              ? "Your purchase orders have been created successfully."
              : "Your purchase order has been created successfully."),
      });
    },
  };

  const runSubButton = async (btn) => {
    const enabled = asBool(btn?.enabled, true);
    if (!btn?.function || !enabled) return;

    try {
      // default busy while running action (but Purchase will override with message)
      startLoading("");
      await executeAction(btn.function, { ...actionCtxBase, button: btn });
    } catch (e) {
      localPopup.flashError?.(e?.response?.data?.error || e?.message || "Action failed");
    } finally {
      stopLoading();
    }
  };

  const renderMoney = (amount, { showPost = false } = {}) => {
    return (
      <span style={{ whiteSpace: "nowrap" }}>
        {showCurrency && <span style={{ marginRight: 6 }}>{currencyValue}</span>}
        <span style={{ fontWeight: 700 }}>{format2(amount)}</span>
        {showPost && showPostText && (
          <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.75, fontWeight: 400 }}>
            {postPriceValueText}
          </span>
        )}
      </span>
    );
  };

  const renderOrderHeader = () => {
    if (!orderFields) return null;

    return (
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          background: "#fafafa",
          position: "relative",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
          Order details
          {orderHeader?.loading && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, opacity: 0.8 }}>
              <span className="spinner" style={{ width: 14, height: 14 }} />
              Loading order details...
            </span>
          )}
        </div>

        {orderHeader?.error && (
          <div
            style={{
              marginTop: 8,
              padding: 10,
              borderRadius: 10,
              background: "#fee2e2",
              color: "#991b1b",
              fontSize: 13,
            }}
          >
            {orderHeader.error}
          </div>
        )}

        <div
          style={{
            marginTop: 10,
            display: "grid",
            gridTemplateColumns: "220px minmax(0, 1fr)",
            columnGap: 10,
            rowGap: 10,
            alignItems: "center",
            width: "100%",
          }}
        >
          {orderDetailsEntries.map(([field, def]) => {
            const label = def?.label || field;
            const required = !!def?.required;

            const value = orderHeader?.values?.[field] ?? "";

            const opts = Array.isArray(orderHeader?.options?.[field])
              ? orderHeader.options[field]
              : [];

            const mode = String(def?.mode || "").trim().toLowerCase();
            const hasQuery =
              typeof def?.query === "string" &&
              def.query.trim().length > 0;

            /**
             * Een Aanpasbaar veld met een query is ALTIJD een dropdown.
             *
             * Dus:
             * 0 resultaten -> lege/disabled dropdown
             * 1 resultaat  -> dropdown
             * N resultaten -> dropdown
             *
             * Nooit terugvallen naar een vrij tekstveld voor een FK-query.
             */
            const isSelect = mode !== "fixed" && hasQuery;


            // Bepaal of deze dropdown afhankelijk is van een ander header-veld.
            const dependencyFields = (
              Array.isArray(def?.queryParams)
                ? def.queryParams
                : []
            )
              .filter(
                (param) =>
                  typeof param === "string" &&
                  param.trim().toUpperCase().startsWith("HEADER:")
              )
              .map((param) =>
                param.split(":", 2)[1]?.trim()
              )
              .filter(Boolean);


            const missingDependencyFields = dependencyFields.filter(
              (dependencyField) => {
                const dependencyValue =
                  orderHeader?.values?.[dependencyField];

                return (
                  dependencyValue === null ||
                  dependencyValue === undefined ||
                  String(dependencyValue).trim() === ""
                );
              }
            );


            const missingDependencyLabels =
              missingDependencyFields.map(
                (dependencyField) =>
                  orderFields?.[dependencyField]?.label ||
                  dependencyField
              );


            let emptyOptionLabel = "-- select --";

            if (isSelect && opts.length === 0) {
              if (missingDependencyLabels.length > 0) {
                emptyOptionLabel =
                  `-- select ${missingDependencyLabels.join(", ")} first --`;
              } else {
                emptyOptionLabel =
                  "-- no options available --";
              }
            }


            const disabled =
              orderHeader?.loading ||
              busyState.on ||
              (isSelect && opts.length === 0);

            return (
              <React.Fragment key={field}>
                <div style={{ fontSize: 13, opacity: 0.85 }}>
                  {label}
                  {required && <span style={{ color: "#dc2626", fontWeight: 900, marginLeft: 4 }}>*</span>}
                </div>

                <div style={{ minWidth: 0 }}>
                  {isSelect ? (
                    <select
                      value={value}
                      disabled={disabled}
                      onChange={(e) =>
                        setOrderFieldValue(
                          field,
                          e.target.value
                        )
                      }
                      style={{
                        width: "100%",
                        height: 34,
                        borderRadius: 10,
                        border: "1px solid #ddd",
                        padding: "0 10px",
                        background: disabled
                          ? "#f3f4f6"
                          : "#fff",
                        boxSizing: "border-box",
                      }}
                    >
                      <option value="">
                        {emptyOptionLabel}
                      </option>

                      {opts.map((o) => (
                        <option
                          key={String(o.value)}
                          value={o.value}
                        >
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={value}
                      disabled={disabled}
                      onChange={(e) => setOrderFieldValue(field, e.target.value)}
                      placeholder={label}
                      style={{
                        width: "100%",
                        height: 34,
                        borderRadius: 10,
                        border: "1px solid #ddd",
                        padding: "0 10px",
                        background: disabled ? "#f3f4f6" : "#fff",
                        boxSizing: "border-box",
                        minWidth: 0,
                      }}
                    />
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  const closeSuccessAndCart = () => {
    setSuccessDialog({
      open: false,
      title: "Purchase order created",
      orderNumber: null,
      text: "",
    });

    closeCart?.();
  };

  return (
    <>
      <div style={{ position: "fixed", inset: 0, zIndex: 9999 }}>
        {/* overlay (outside popup) */}
        <div
          onClick={busyState.on ? undefined : closeCart}
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            cursor: "default",
          }}
        />

        {/* popup */}
        <div
          style={{
            position: "absolute",
            right: 24,
            top: 24,
            bottom: 24,
            width: "min(1020px, calc(100% - 48px))",
            background: "#fff",
            borderRadius: 14,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ✅ Busy overlay INSIDE popup */}
          {busyState.on && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.72)",
                zIndex: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: 18,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.10)",
                  minWidth: 280,
                  maxWidth: 520,
                  textAlign: "center",
                }}
              >
                <div className="spinner" style={{ margin: "0 auto 10px" }} />
                <div style={{ fontSize: 13, color: "#4b5563" }}>
                  {busyState.message || "Loading..."}
                </div>
              </div>
            </div>
          )}

          {/* Error dialog */}
          {errorDialog.open && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.45)",
                zIndex: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  width: "min(520px, 100%)",
                  background: "#fff",
                  borderRadius: 14,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.30)",
                  border: "1px solid #e5e7eb",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: 16,
                    borderBottom: "1px solid #eee",
                    fontWeight: 800,
                    fontSize: 16,
                  }}
                >
                  {errorDialog.title || "Unable to continue"}
                </div>

                <div
                  style={{
                    padding: 16,
                    whiteSpace: "pre-line",
                    fontSize: 14,
                    color: "#111827",
                  }}
                >
                  {errorDialog.text}
                </div>

                <div
                  style={{
                    padding: 16,
                    borderTop: "1px solid #eee",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    className="create-button"
                    onClick={() =>
                      setErrorDialog({
                        open: false,
                        title: "Unable to continue",
                        text: "",
                      })
                    }
                  >
                    Ok
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ✅ Success dialog */}
          {successDialog.open && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                zIndex: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  width: "min(520px, 100%)",
                  background: "#fff",
                  borderRadius: 14,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
                  border: "1px solid #e5e7eb",
                  overflow: "hidden",
                }}
              >
                <div style={{ padding: 16, borderBottom: "1px solid #eee", fontWeight: 800 }}>
                  {successDialog.title || "Purchase order created"}
                </div>

                <div style={{ padding: 16, whiteSpace: "pre-line", fontSize: 14, color: "#111827" }}>
                  {successDialog.text}
                </div>

                <div
                  style={{
                    padding: 16,
                    borderTop: "1px solid #eee",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                  }}
                >
                  <button className="create-button" onClick={closeSuccessAndCart}>
                    Ok
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* header */}
          <div
            style={{
              padding: 16,
              borderBottom: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700 }}>{cartConfig?.label || "Shopping cart"}</div>

            <button
              className="create-button"
              onClick={busyState.on ? undefined : closeCart}
              disabled={busyState.on}
              title="Close"
              aria-label="Close"
              style={{
                width: 40,
                minWidth: 40,
                height: 40,
                padding: 0,
                borderRadius: 12,
                justifyContent: "center",
              }}
            >
              ✕
            </button>
          </div>

          {/* Sticky order details + scrollable lines */}
          <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
            <div
              style={{
                padding: 16,
                paddingBottom: 12,
                borderBottom: "1px solid #eee",
                flexShrink: 0,
                background: "#fff",
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}
            >
              {renderOrderHeader()}
            </div>

            <div style={{ padding: 16, overflow: "auto", flex: 1, minHeight: 0 }}>
              {normalized.length === 0 && <div style={{ opacity: 0.7 }}>Your cart is empty.</div>}

              {normalized.map((it) => {
                let raw = null;
                if (priceField) raw = it._data?.[priceField];
                if (raw == null) {
                  const fallbackField = (it._fields || []).find((f) => String(f).toUpperCase().includes("PRICE"));
                  raw = fallbackField ? it._data?.[fallbackField] : null;
                }
                const unitPrice = raw != null ? Number(raw) : 0;
                const qty = Number(it.quantity || 1);
                const lineTotal = unitPrice * qty;

                const imgVal =
                  getFirstExisting(it._data, [inferredKeys.imageKey, "ITEM_THUMB_IMAGE", "THUMB_IMAGE", "IMAGE"]) || null;

                const codeVal = getFirstExisting(it._data, [inferredKeys.codeKey, "ITEM_CODE", "CODE"]) || "";
                const descVal =
                  getFirstExisting(it._data, [inferredKeys.descKey, "ITEM_DESCRIPTION", "DESCRIPTION"]) || "";

                const imgSrc = isImageField(inferredKeys.imageKey) ? buildImageSrc(imgVal) : buildImageSrc(imgVal);

                return (
                  <div
                    key={`${it.company_id}-${it.user_id}-${it.module_id}-${it.record_id}`}
                    style={{
                      border: "1px solid #eee",
                      borderRadius: 12,
                      padding: 12,
                      marginBottom: 12,
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: 12,
                    }}
                  >
                    <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt=""
                          style={{
                            width: 56,
                            height: 56,
                            maxHeight: 56,
                            objectFit: "cover",
                            borderRadius: 10,
                            flex: "0 0 auto",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: 10,
                            background: "#f2f2f2",
                            flex: "0 0 auto",
                          }}
                        />
                      )}

                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>{String(codeVal || "")}</div>
                        <div
                          style={{
                            opacity: 0.85,
                            fontSize: 13,
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                          }}
                        >
                          {String(descVal || "")}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        justifyContent: "flex-end",
                        minWidth: 300,
                      }}
                    >
                      <div style={{ minWidth: 160, textAlign: "right" }}>{renderMoney(unitPrice, { showPost: false })}</div>

                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, opacity: 0.7 }}>Qty</span>
                        <input
                          type="number"
                          min={1}
                          max={999}
                          inputMode="numeric"
                          value={it.quantity || 1}
                          onChange={(e) => {
                            const rawV = e.target.value;
                            if (rawV === "") {
                              setQuantity(it, 1);
                              return;
                            }
                            let q = parseInt(rawV, 10);
                            if (!Number.isFinite(q)) q = 1;
                            q = Math.max(1, Math.min(999, q));
                            setQuantity(it, q);
                          }}
                          onBlur={(e) => {
                            let q = parseInt(e.target.value, 10);
                            if (!Number.isFinite(q)) q = 1;
                            q = Math.max(1, Math.min(999, q));
                            if (q !== Number(it.quantity || 1)) setQuantity(it, q);
                          }}
                          disabled={busyState.on}
                          style={{
                            width: 70,
                            height: 34,
                            padding: "0 8px",
                            borderRadius: 10,
                            border: "1px solid #ddd",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>

                      <div style={{ minWidth: 170, textAlign: "right" }}>{renderMoney(lineTotal, { showPost: true })}</div>

                      <button
                        className="create-button cart-remove-btn"
                        onClick={() => removeItem(it)}
                        disabled={busyState.on}
                        title="Remove"
                        aria-label="Remove"
                        style={{
                          alignSelf: "center",
                          width: 40,
                          minWidth: 40,
                          height: 34,
                          padding: 0,
                          borderRadius: 12,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 0,
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path
                            fill="currentColor"
                            d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9zm-1 13h12a2 2 0 0 0 2-2V9H4v11a2 2 0 0 0 2 2z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* footer */}
          <div
            style={{
              padding: 16,
              borderTop: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            <div style={{ fontSize: 14 }}>
              {normalized.length ? (
                <>
                  <span style={{ opacity: 0.75, marginRight: 8 }}>Total:</span>
                  {renderMoney(total, { showPost: true })}
                </>
              ) : (
                ""
              )}
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {subButtons.map((b) => {
                const enabled = asBool(b.enabled, true);
                return (
                  <button
                    key={b.label || b.function}
                    className="create-button"
                    disabled={!enabled || busyState.on}
                    onClick={() => runSubButton(b)}
                    title={b.tooltip || ""}
                    style={{
                      width: b.width || undefined,
                      height: b.height || undefined,
                      opacity: !enabled || busyState.on ? 0.7 : 1,
                      cursor: !enabled || busyState.on ? "not-allowed" : "pointer",
                    }}
                  >
                    {b.label || b.function}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
        
      {localPopup.view}
    </>
  );
}
